import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  LinearProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Calculate as CalculateIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { Transaction } from '../types';
import { loanCalculator, FinancialProfile, LoanCapacityResult } from '../services/loanCalculator';

interface LoanCalculatorProps {
  transactions: Transaction[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`loan-tabpanel-${index}`}
      aria-labelledby={`loan-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const LoanCalculator: React.FC<LoanCalculatorProps> = ({ transactions }) => {
  const [tabValue, setTabValue] = useState(0);
  const [profile, setProfile] = useState<FinancialProfile>({
    monthlyIncome: 0,
    monthlyExpenses: 0,
    existingDebts: 0,
    savings: 0,
    age: 30,
    employmentType: 'cdi',
    hasGuarantor: false,
    downPayment: 0,
  });
  const [result, setResult] = useState<LoanCapacityResult | null>(null);
  const [loading, setLoading] = useState(false);

  // Auto-calculer les revenus/dépenses depuis les transactions
  useEffect(() => {
    if (transactions.length > 0) {
      const now = new Date();
      const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      
      const recentTransactions = transactions.filter(t => new Date(t.date) >= threeMonthsAgo);
      
      const monthlyIncome = recentTransactions
        .filter(t => t.type === 'income')
        .reduce((sum, t) => sum + t.amount, 0) / 3;
        
      const monthlyExpenses = recentTransactions
        .filter(t => t.type === 'expense')
        .reduce((sum, t) => sum + t.amount, 0) / 3;

      setProfile(prev => ({
        ...prev,
        monthlyIncome: Math.round(monthlyIncome),
        monthlyExpenses: Math.round(monthlyExpenses)
      }));
    }
  }, [transactions]);

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const loanResult = loanCalculator.calculateLoanCapacity(transactions, profile);
      setResult(loanResult);
      setTabValue(1); // Passer aux résultats
    } catch (error) {
      console.error('Erreur lors du calcul:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      default: return 'info';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 750) return 'success';
    if (score >= 650) return 'warning';
    return 'error';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AccountBalanceIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              🏦 Calculateur de Capacité d'Emprunt
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Estimez votre capacité d'emprunt et optimisez votre dossier
            </Typography>
          </Box>
        </Box>

        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
          <Tab icon={<InfoIcon />} label="Profil Financier" />
          <Tab icon={<AssessmentIcon />} label="Résultats" disabled={!result} />
          <Tab icon={<CalculateIcon />} label="Simulations" disabled={!result} />
        </Tabs>

        <Divider sx={{ mb: 3 }} />

        {/* Onglet 1: Profil Financier */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon />
                  💰 Revenus & Dépenses
                </Typography>
                
                <TextField
                  fullWidth
                  label="Revenus mensuels nets"
                  type="number"
                  value={profile.monthlyIncome}
                  onChange={(e) => setProfile({ ...profile, monthlyIncome: Number(e.target.value) })}
                  margin="normal"
                  InputProps={{ endAdornment: '€' }}
                  helperText="Calculé automatiquement depuis vos transactions"
                />
                
                <TextField
                  fullWidth
                  label="Charges mensuelles"
                  type="number"
                  value={profile.monthlyExpenses}
                  onChange={(e) => setProfile({ ...profile, monthlyExpenses: Number(e.target.value) })}
                  margin="normal"
                  InputProps={{ endAdornment: '€' }}
                  helperText="Calculé automatiquement depuis vos transactions"
                />
                
                <TextField
                  fullWidth
                  label="Crédits existants (mensuel)"
                  type="number"
                  value={profile.existingDebts}
                  onChange={(e) => setProfile({ ...profile, existingDebts: Number(e.target.value) })}
                  margin="normal"
                  InputProps={{ endAdornment: '€' }}
                />
                
                <TextField
                  fullWidth
                  label="Épargne disponible"
                  type="number"
                  value={profile.savings}
                  onChange={(e) => setProfile({ ...profile, savings: Number(e.target.value) })}
                  margin="normal"
                  InputProps={{ endAdornment: '€' }}
                />
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon />
                  👤 Profil Personnel
                </Typography>
                
                <TextField
                  fullWidth
                  label="Âge"
                  type="number"
                  value={profile.age}
                  onChange={(e) => setProfile({ ...profile, age: Number(e.target.value) })}
                  margin="normal"
                  inputProps={{ min: 18, max: 80 }}
                />
                
                <FormControl fullWidth margin="normal">
                  <InputLabel>Type d'emploi</InputLabel>
                  <Select
                    value={profile.employmentType}
                    onChange={(e) => setProfile({ ...profile, employmentType: e.target.value as any })}
                    label="Type d'emploi"
                  >
                    <MenuItem value="cdi">CDI</MenuItem>
                    <MenuItem value="cdd">CDD</MenuItem>
                    <MenuItem value="freelance">Freelance</MenuItem>
                    <MenuItem value="unemployed">Sans emploi</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  label="Apport personnel"
                  type="number"
                  value={profile.downPayment}
                  onChange={(e) => setProfile({ ...profile, downPayment: Number(e.target.value) })}
                  margin="normal"
                  InputProps={{ endAdornment: '€' }}
                  helperText="10-20% du projet immobilier recommandé"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={profile.hasGuarantor}
                      onChange={(e) => setProfile({ ...profile, hasGuarantor: e.target.checked })}
                    />
                  }
                  label="J'ai une caution solidaire"
                />
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleCalculate}
              disabled={loading || profile.monthlyIncome === 0}
              startIcon={loading ? <LinearProgress /> : <CalculateIcon />}
              sx={{ px: 4, py: 1.5 }}
            >
              {loading ? 'Calcul en cours...' : 'Calculer ma capacité d\'emprunt'}
            </Button>
          </Box>
        </TabPanel>

        {/* Onglet 2: Résultats */}
        <TabPanel value={tabValue} index={1}>
          {result && (
            <Box>
              {/* Résultats principaux */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
                <Card sx={{ backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" fontWeight="bold">
                      {formatCurrency(result.maxLoanAmount)}
                    </Typography>
                    <Typography variant="body1">
                      💰 Capacité d'emprunt max
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card sx={{ backgroundColor: 'success.light', color: 'success.contrastText' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" fontWeight="bold">
                      {formatCurrency(result.maxMonthlyPayment)}
                    </Typography>
                    <Typography variant="body1">
                      📅 Mensualité maximale
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card sx={{ backgroundColor: 'info.light', color: 'info.contrastText' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" fontWeight="bold">
                      {result.creditScore}
                    </Typography>
                    <Typography variant="body1">
                      ⭐ Score de crédit estimé
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Indicateurs de risque */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>📊 Analyse de votre profil</Typography>
                  
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Taux d'endettement: {formatPercentage(result.debtToIncomeRatio)}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min(100, result.debtToIncomeRatio * 100 / 0.35)} 
                        color={result.debtToIncomeRatio > 0.33 ? 'error' : result.debtToIncomeRatio > 0.25 ? 'warning' : 'success'}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Maximum recommandé: 35%
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip 
                        icon={result.riskLevel === 'low' ? <CheckCircleIcon /> : <WarningIcon />}
                        label={`Risque ${result.riskLevel === 'low' ? 'faible' : result.riskLevel === 'medium' ? 'modéré' : 'élevé'}`} 
                        color={getRiskColor(result.riskLevel) as any}
                        variant="filled"
                      />
                      <Chip 
                        label={`Score: ${result.creditScore}`}
                        color={getScoreColor(result.creditScore) as any}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Recommandations */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>💡 Recommandations personnalisées</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {result.recommendations.map((rec, index) => (
                      <Alert 
                        key={index}
                        severity={rec.includes('🔴') ? 'error' : rec.includes('🟡') ? 'warning' : 'info'}
                        sx={{ '& .MuiAlert-message': { width: '100%' } }}
                      >
                        {rec}
                      </Alert>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </TabPanel>

        {/* Onglet 3: Simulations */}
        <TabPanel value={tabValue} index={2}>
          {result && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalculateIcon />
                📈 Simulations de prêt
              </Typography>
              
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Montant</strong></TableCell>
                      <TableCell><strong>Durée</strong></TableCell>
                      <TableCell><strong>Taux</strong></TableCell>
                      <TableCell><strong>Mensualité</strong></TableCell>
                      <TableCell><strong>Coût total</strong></TableCell>
                      <TableCell><strong>Intérêts</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.loanSimulations.slice(0, 9).map((sim, index) => (
                      <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                        <TableCell>
                          <Typography fontWeight="bold" color="primary">
                            {formatCurrency(sim.loanAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell>{sim.duration} ans</TableCell>
                        <TableCell>{formatPercentage(sim.interestRate)}</TableCell>
                        <TableCell>{formatCurrency(sim.monthlyPayment)}</TableCell>
                        <TableCell>{formatCurrency(sim.totalAmount)}</TableCell>
                        <TableCell>
                          <Typography color={sim.totalInterest > sim.loanAmount * 0.3 ? 'error' : 'text.primary'}>
                            {formatCurrency(sim.totalInterest)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  💡 <strong>Conseil:</strong> Les taux d'intérêt varient selon votre profil et la banque. 
                  Ces simulations sont indicatives. Comparez plusieurs offres avant de vous engager.
                </Typography>
              </Alert>
            </Box>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default LoanCalculator;
